/* BYLOS — Atelier: fit finder, colour studio, conversion tracking */
(() => {
  const d = document;
  const html = d.documentElement;

  /* ================= Tracking =================
     Every event goes to Shopify's customer-event bus as "bylos_<name>".
     A custom pixel (Settings -> Customer events) forwards them to GA4. */
  const device = () => (matchMedia('(max-width: 899px)').matches ? 'mobile' : 'desktop');
  const track = (name, data = {}) => {
    const payload = { ...data, device: device(), experiment: html.dataset.exp || null, variant: html.dataset.variant || null, path: location.pathname };
    try { window.Shopify?.analytics?.publish?.(`bylos_${name}`, payload); } catch (e) { /* no-op */ }
    if (window.BYLOS_DEBUG) console.info('[bylos]', name, payload);
  };
  window.BYLOS = window.BYLOS || {};
  window.BYLOS.track = track;

  const productSection = () => d.querySelector('[data-product]');
  const onLoadProduct = () => {
    const s = productSection();
    if (s && s.dataset.colour) track('colour_view', { colour: s.dataset.colour, product: s.dataset.title, source: 'page' });
  };

  d.addEventListener('click', (e) => {
    if (!e.isTrusted) return;
    const t = e.target.closest('[data-pair-toggle], [data-set-toggle]');
    if (!t || t.getAttribute('aria-expanded') === 'true') return; // only count opens
    track(t.hasAttribute('data-set-toggle') ? 'set_open' : 'second_pair_open', { product: productSection()?.dataset.title });
  }, true);

  /* ================= Fit finder ================= */
  const fitState = { usual: 'M', pref: 'classic', waist: 78, inches: false };
  const usualIndex = { XS: 0, S: 0, M: 1, L: 2, XL: 3 };
  const fitEl = () => d.querySelector('[data-fit]');
  let lastFocus = null;

  const fmtWaist = (cm) => (fitState.inches ? `${(cm / 2.54).toFixed(1).replace(/\.0$/, '')}"` : `${cm} cm`);

  const openFit = () => {
    const el = fitEl(); if (!el) return;
    lastFocus = d.activeElement;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-open'));
    html.classList.add('no-scroll');
    el.querySelector('[data-fit-ask]').hidden = false;
    el.querySelector('[data-fit-result]').hidden = true;
    setTimeout(() => el.querySelector('.fit__x')?.focus(), 60);
    track('fit_finder_open', { product: productSection()?.dataset.title });
  };
  const closeFit = () => {
    const el = fitEl(); if (!el || el.hidden) return;
    el.classList.remove('is-open'); html.classList.remove('no-scroll');
    setTimeout(() => { el.hidden = true; }, 380);
    lastFocus?.focus?.();
  };

  const recommend = (chart) => {
    const w = fitState.waist; const last = chart.length - 1;
    let i = chart.findIndex((c) => w >= c.min && w < c.max);
    let edge = null;
    if (i === -1) { if (w < chart[0].min) { i = 0; edge = 'below'; } else { i = last; edge = 'above'; } }
    const base = chart[i];
    const span = base.max - base.min;
    const pos = (w - base.min) / span; // 0 -> bottom of range, 1 -> top
    let moved = null;
    if (!edge && fitState.pref === 'relaxed' && pos >= 0.7 && i < last) { i += 1; moved = 'up'; }
    if (!edge && fitState.pref === 'snug' && pos <= 0.3 && i > 0) { i -= 1; moved = 'down'; }
    const rec = chart[i];

    const where = pos < 0.34 ? 'the lower end' : pos < 0.67 ? 'the middle' : 'the upper end';
    const feel = { snug: 'a sculpted, second-skin fit', classic: 'the fit BYLOS is designed for', relaxed: 'a little more ease' }[fitState.pref];
    let why;
    if (edge === 'below') why = `Your waist (${fmtWaist(w)}) is just under our smallest range, so ${rec.name} is your size. It will sit snug, as a brief should.`;
    else if (edge === 'above') why = `Your waist (${fmtWaist(w)}) is above our ${rec.name} range (${rec.min}–${rec.max} cm). ${rec.name} will feel firm. Email contact@bylos.com.au and we'll help you choose.`;
    else if (moved === 'up') why = `Your waist (${fmtWaist(w)}) sits at the top of ${base.name} (${base.min}–${base.max} cm). Since you like ${feel}, we'd size up to ${rec.name}.`;
    else if (moved === 'down') why = `Your waist (${fmtWaist(w)}) sits at the bottom of ${base.name} (${base.min}–${base.max} cm). For ${feel}, ${rec.name} will hold closer.`;
    else why = `Your waist (${fmtWaist(w)}) sits at ${where} of our ${rec.name} range (${rec.min}–${rec.max} cm), giving you ${feel}.`;

    const ui = usualIndex[fitState.usual];
    if (ui !== undefined && Math.min(ui, last) !== i) why += ` You usually wear ${fitState.usual}, but for a brief your waist is the better guide. BYLOS is cut true to size.`;
    else why += ' BYLOS is cut true to size, so your usual size should feel familiar.';
    return { rec, why };
  };

  const showResult = () => {
    const el = fitEl(); if (!el) return;
    let chart = [];
    try { chart = JSON.parse(el.dataset.chart || '[]'); } catch (e) { chart = []; }
    if (!chart.length) return;
    const { rec, why } = recommend(chart);
    el.querySelector('[data-fit-size]').textContent = rec.name;
    el.querySelector('[data-fit-why]').textContent = why;
    const apply = el.querySelector('[data-fit-apply]');
    apply.dataset.size = rec.name;
    const chip = productSection()?.querySelector(`.option [data-value="${CSS.escape(rec.name)}"]`);
    const soldOut = chip?.classList.contains('is-soldout');
    apply.textContent = soldOut ? `${rec.name} is sold out in this colour` : `Select ${rec.name}`;
    apply.disabled = !!soldOut;
    el.querySelector('[data-fit-ask]').hidden = true;
    const res = el.querySelector('[data-fit-result]');
    res.hidden = false; res.classList.remove('is-in'); void res.offsetWidth; res.classList.add('is-in');
    track('fit_finder_complete', { recommended: rec.name, waist_cm: fitState.waist, usual: fitState.usual, preference: fitState.pref, sold_out: !!soldOut });
  };

  d.addEventListener('click', (e) => {
    if (e.target.closest('[data-fit-open]')) { e.preventDefault(); openFit(); return; }
    const el = fitEl(); if (!el || el.hidden) return;
    if (e.target.closest('[data-fit-close]')) { closeFit(); return; }
    const opt = e.target.closest('[data-fit-group] [data-val]');
    if (opt) {
      const g = opt.closest('[data-fit-group]');
      g.querySelectorAll('[data-val]').forEach((b) => b.setAttribute('aria-checked', String(b === opt)));
      fitState[g.dataset.fitGroup] = opt.dataset.val;
      return;
    }
    if (e.target.closest('[data-fit-unit]')) {
      fitState.inches = !fitState.inches;
      e.target.closest('[data-fit-unit]').textContent = fitState.inches ? 'Show centimetres' : 'Show inches';
      el.querySelector('[data-fit-waist-out]').textContent = fmtWaist(fitState.waist);
      return;
    }
    if (e.target.closest('[data-fit-go]')) { showResult(); return; }
    if (e.target.closest('[data-fit-again]')) { el.querySelector('[data-fit-ask]').hidden = false; el.querySelector('[data-fit-result]').hidden = true; return; }
    const apply = e.target.closest('[data-fit-apply]');
    if (apply && !apply.disabled) {
      const chip = productSection()?.querySelector(`.option [data-value="${CSS.escape(apply.dataset.size)}"]`);
      chip?.click();
      track('fit_size_applied', { size: apply.dataset.size });
      closeFit();
      chip?.focus?.();
    }
  });
  d.addEventListener('input', (e) => {
    const r = e.target.closest('[data-fit-waist]'); if (!r) return;
    fitState.waist = Number(r.value);
    fitEl()?.querySelector('[data-fit-waist-out]') && (fitEl().querySelector('[data-fit-waist-out]').textContent = fmtWaist(fitState.waist));
    r.style.setProperty('--p', `${((r.value - r.min) / (r.max - r.min)) * 100}%`);
  });
  d.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeFit(); });

  /* ================= Colour studio =================
     Tap a colour: the next brief's photos cross-fade in over the current ones,
     and the panel swaps to that product without a page load. */
  let busy = false;
  const preload = (img) => new Promise((res) => {
    if (!img) return res();
    const p = new Image();
    if (img.getAttribute('sizes')) p.sizes = img.getAttribute('sizes');
    if (img.getAttribute('srcset')) p.srcset = img.getAttribute('srcset');
    p.src = img.getAttribute('src');
    const done = () => res();
    (p.decode ? p.decode().then(done, done) : (p.onload = done));
    setTimeout(done, 1200);
  });

  const studioSwap = async (dot) => {
    const wrap = dot.closest('.shopify-section');
    const url = dot.dataset.studioUrl;
    if (!wrap || !wrap.id) { location.href = url; return; }
    busy = true;
    const sid = wrap.id.replace('shopify-section-', '');
    const group = dot.closest('[data-studio]');
    group.querySelectorAll('[data-studio-url]').forEach((x) => x.setAttribute('aria-checked', String(x === dot)));
    const nameEl = group.querySelector('[data-studio-name]'); if (nameEl) nameEl.textContent = dot.dataset.name;
    try {
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(sid)}`, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error('fetch');
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.getElementById(wrap.id) || doc.querySelector('.shopify-section') || doc.body;
      await preload(fresh.querySelector('.gallery__item img'));

      const old = wrap.querySelector('.gallery-wrap');
      let ghost = null;
      if (old) {
        const r = old.getBoundingClientRect();
        const sl = old.querySelector('.gallery')?.scrollLeft || 0;
        ghost = old.cloneNode(true);
        ghost.classList.add('studio-ghost');
        Object.assign(ghost.style, { position: 'absolute', left: `${r.left + scrollX}px`, top: `${r.top + scrollY}px`, width: `${r.width}px`, height: `${r.height}px`, margin: '0', zIndex: '4', pointerEvents: 'none' });
        d.body.appendChild(ghost);
        const gg = ghost.querySelector('.gallery'); if (gg) gg.scrollLeft = sl;
      }
      const panelScroll = wrap.querySelector('.panel__inner')?.scrollTop || 0;
      wrap.innerHTML = fresh.innerHTML;
      const pi = wrap.querySelector('.panel__inner');
      if (pi) { pi.scrollTop = panelScroll; pi.classList.add('is-studio-in'); }
      wrap.querySelectorAll('[data-product]').forEach((s) => window.BYLOS.initProduct?.(s));
      if (ghost) { requestAnimationFrame(() => requestAnimationFrame(() => ghost.classList.add('is-out'))); setTimeout(() => ghost.remove(), 800); }

      const s = wrap.querySelector('[data-product]');
      history.replaceState({}, '', url);
      if (s?.dataset.title) d.title = `${s.dataset.title} — BYLOS`;
      track('colour_view', { colour: dot.dataset.name, product: s?.dataset.title, source: 'studio' });
    } catch (err) {
      location.href = url;
    } finally { busy = false; }
  };

  d.addEventListener('click', (e) => {
    const dot = e.target.closest('[data-studio-url]');
    if (!dot || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    if (busy || dot.getAttribute('aria-checked') === 'true') return;
    studioSwap(dot);
  });
  // Hovering a dot previews its name
  d.addEventListener('pointerover', (e) => {
    const dot = e.target.closest('[data-studio-url]'); if (!dot) return;
    const n = dot.closest('[data-studio]')?.querySelector('[data-studio-name]'); if (n) n.textContent = dot.dataset.name;
  });
  d.addEventListener('pointerout', (e) => {
    const dot = e.target.closest('[data-studio-url]'); if (!dot) return;
    const g = dot.closest('[data-studio]'); const cur = g?.querySelector('[aria-checked="true"]');
    const n = g?.querySelector('[data-studio-name]'); if (n && cur) n.textContent = cur.dataset.name;
  });

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', onLoadProduct); else onLoadProduct();
})();
